/**
 * HL-11440 §11 — Mixpanel events for the page-per-question (Variant B) questionnaire.
 *
 * Four events; question identity lives in the properties, never in the name:
 *   questionnaire_screen_viewed    a screen's content rendered (question, follow-up, interstitial)
 *   questionnaire_question_answered Next clicked and validation passed
 *   questionnaire_question_error    validation failed, or a save failed
 *   questionnaire_blocker_shown     B1 (under-18 DOB), B2 (BMI gate), B3 (pregnancy / contraindication)
 *
 * Rules from the ticket applied here:
 *   - `qversion` on every event: "v2" for the weight_loss_3 questionnaire, "control" otherwise.
 *   - `question_id` is stable and config-driven: "q{recipe_question_id}" for a question page,
 *     "q{recipe_question_id}-s{answer_id}" for a follow-up sub-page, and a fixed slug for the
 *     interstitial screens (intro, weight-projection, ...). Never an index, so reordering in the
 *     BO does not change an id.
 *   - Option ids, never labels. Free text is never sent — only has_text / text_length.
 */

// HL-12122: not "View Questionnaire Page" — that one fires once per questionnaire load (Questionnaire.tsx),
// so it stays comparable between the one-pager and page-by-page test cells.
export const QUESTIONNAIRE_SCREEN_VIEWED_EVENT = "questionnaire_screen_viewed";
export const QUESTION_ANSWERED_EVENT = "questionnaire_question_answered";
export const QUESTION_ERROR_EVENT = "questionnaire_question_error";
export const BLOCKER_SHOWN_EVENT = "questionnaire_blocker_shown";

/**
 * HL-12029 §4 — Health Summary "edit answers" A/B. Every event carries `variant` (A/B) and
 * `funnel` (wl2/wl3); `summary_continue_clicked` adds `edited`, `summary_edit_clicked`
 * (arm B only) adds `section`.
 */
export const SUMMARY_PAGE_VIEWED_EVENT = "summary_page_viewed";
export const SUMMARY_CONTINUE_CLICKED_EVENT = "summary_continue_clicked";
export const SUMMARY_EDIT_CLICKED_EVENT = "summary_edit_clicked";
export type SummaryEditSection = "goal" | "health_profile" | string;

export type Qversion = "v2" | "control";
export type BlockerId = "B1" | "B2" | "B3";
export type QuestionErrorType = "validation" | "save_failed" | "network";
export type BmiBand = "under_27" | "27_29" | "30_plus";

const WL3_MENTION = "weight_loss_3";

/** Ticket §11: values are exactly "v2" (the WL3 short questionnaire) and "control". */
export const getQversion = (mention: unknown): Qversion =>
  String(mention ?? "").trim().toLowerCase() === WL3_MENTION ? "v2" : "control";

/** Ticket §4 bands: under 27 / 27 to 29.9 / 30 or above. */
export const getBmiBand = (bmi: number | null | undefined): BmiBand | undefined => {
  if (bmi === null || bmi === undefined || !Number.isFinite(bmi)) return undefined;
  if (bmi < 27) return "under_27";
  if (bmi < 30) return "27_29";
  return "30_plus";
};

export const getDevice = (): "mobile" | "desktop" => {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth < 768 ? "mobile" : "desktop";
};

export const getLocale = (language: unknown, country: unknown): string =>
  `${String(language ?? "").toLowerCase()}-${String(country ?? "").toUpperCase()}`;

/** Minimal shape of navigationHelpers.getPageContent() this module reads. */
export interface PageContentLike {
  question?: { id?: number | string } | null;
  answerGroup?: { recipe?: { id?: number | string; type?: string }; children?: any[] } | null;
  questionType?: string | null;
}

/** Stable, config-driven id for a question page or a follow-up sub-page. */
export const getQuestionId = (pageContent: PageContentLike | null | undefined): string | undefined => {
  const questionId = pageContent?.question?.id;
  if (questionId === undefined || questionId === null) return undefined;
  const isSubPage = String(pageContent?.questionType ?? "").startsWith("subquestion_");
  const answerId = pageContent?.answerGroup?.recipe?.id;
  return isSubPage && answerId !== undefined && answerId !== null ? `q${questionId}-s${answerId}` : `q${questionId}`;
};

/** Interstitial screens (no DB question behind them) get a fixed slug. */
export const SCREEN_IDS: Record<string, string> = {
  intro: "intro",
  weightProjection: "weight-projection",
  realResults: "real-results",
  socialProof: "social-proof",
  healthSummary: "health-summary",
  contactDetails: "contact-details",
  special: "blocked",
};

const TEXT_TYPES = ["number", "textarea", "text", "date"];

export interface AnswerProps {
  answer_option_ids: Array<number | string>;
  answer_count: number;
  has_text: boolean;
  text_length: number;
}

/**
 * What was answered on a page, without the answer itself for free-text fields.
 * Option pages → the selected option ids. Text/number/date pages → has_text and
 * text_length only (health data never reaches Mixpanel).
 */
export const getAnswerProps = (
  pageContent: PageContentLike | null | undefined,
  answersMap: Record<string | number, any> | null | undefined
): AnswerProps => {
  const empty: AnswerProps = { answer_option_ids: [], answer_count: 0, has_text: false, text_length: 0 };
  const answerGroup = pageContent?.answerGroup;
  if (!answerGroup?.recipe?.id || !answersMap) return empty;

  const type = String(answerGroup.recipe.type ?? "");
  if (TEXT_TYPES.includes(type)) {
    const raw = answersMap[answerGroup.recipe.id];
    const text = raw === null || raw === undefined ? "" : String(raw);
    return { answer_option_ids: [], answer_count: text ? 1 : 0, has_text: text.length > 0, text_length: text.length };
  }

  const ids = (answerGroup.children ?? [])
    .filter((child: any) => child?.recipe?.id !== undefined && !!answersMap[child.recipe.id])
    .map((child: any) => child.recipe.id);
  return { answer_option_ids: ids, answer_count: ids.length, has_text: false, text_length: 0 };
};

/** First selected option that carries a contraindication significance, else the first selected option. */
export const getBlockingAnswerId = (
  pageContent: PageContentLike | null | undefined,
  answersMap: Record<string | number, any> | null | undefined
): number | string | undefined => {
  const children = pageContent?.answerGroup?.children ?? [];
  if (!answersMap) return undefined;
  const selected = children.filter((child: any) => child?.recipe?.id !== undefined && !!answersMap[child.recipe.id]);
  const contraindicated = selected.find((child: any) =>
    child.recipe?.significances?.some((sig: any) => sig?.evaluation_type === "contraindications")
  );
  return (contraindicated ?? selected[0])?.recipe?.id;
};
